import { useCallback, useState } from 'react';
import { api } from '../api/client';
import { useMutation, useResource } from '../api/hooks';
import type { Entitlement, ServerSummary } from '../api/types';
import { isTranslationKey, useI18n } from '../i18n';
import { Badge, Button, Card, Empty, Select, Spinner } from '../components/ui';
import { ErrorBanner } from '../components/ErrorBanner';
import { ConfirmButton } from '../components/Confirm';

export function LicensesPage() {
  const { t, formatDate, formatDuration } = useI18n();
  const entitlements = useResource<Entitlement[]>('/v1/entitlements');
  const servers = useResource<ServerSummary[]>('/v1/servers');
  const [targets, setTargets] = useState<Record<string, string>>({});

  const activate = useMutation<{ id: string; serverId: string }, unknown>(
    useCallback(
      (input) => api.post(`/v1/entitlements/${input.id}/activate`, { serverId: input.serverId }),
      [],
    ),
  );
  const transfer = useMutation<{ id: string; serverId: string }, unknown>(
    useCallback(
      (input) => api.post(`/v1/entitlements/${input.id}/transfer`, { serverId: input.serverId }),
      [],
    ),
  );
  const release = useMutation<string, unknown>(
    useCallback((id: string) => api.post(`/v1/entitlements/${id}/release`), []),
  );

  if (entitlements.loading) return <Spinner label={t('common.loading')} />;

  const list = entitlements.data ?? [];
  const available = servers.data ?? [];

  const kindLabel = (entitlement: Entitlement): string => {
    if (entitlement.allAccess) return t('licenses.allAccess');
    return entitlement.kind === 'MODULE_PERPETUAL'
      ? t('licenses.perpetual')
      : t('licenses.subscription');
  };

  const statusTone = (entitlement: Entitlement): 'good' | 'warn' | 'bad' => {
    if (entitlement.status === 'ACTIVE') return 'good';
    if (entitlement.status === 'GRACE') return 'warn';
    return 'bad';
  };

  const refresh = () => {
    entitlements.reload();
    servers.reload();
  };

  return (
    <div className="stack">
      <h1>{t('licenses.title')}</h1>
      <ErrorBanner
        error={entitlements.error ?? activate.error ?? transfer.error ?? release.error}
      />

      {list.length === 0 ? <Empty>{t('licenses.none')}</Empty> : null}

      <div className="grid">
        {list.map((entitlement) => {
          const statusKey = `licenses.status${entitlement.status}`;
          const target = targets[entitlement.id] ?? available[0]?.id ?? '';
          const busy = activate.pending || transfer.pending || release.pending;

          return (
            <Card
              key={entitlement.id}
              title={entitlement.module?.name ?? t('licenses.allAccess')}
              hint={kindLabel(entitlement)}
            >
              <div className="row wrap">
                <Badge tone={statusTone(entitlement)}>
                  {isTranslationKey(statusKey) ? t(statusKey) : entitlement.status}
                </Badge>
                {entitlement.currentPeriodEnd ? (
                  <span className="muted">
                    {t('licenses.expiresOn', { date: formatDate(entitlement.currentPeriodEnd) })}
                  </span>
                ) : null}
                {entitlement.transferCount > 0 ? (
                  <span className="muted">
                    {t('licenses.transferCount', { count: entitlement.transferCount })}
                  </span>
                ) : null}
              </div>

              <p>
                {entitlement.activation
                  ? t('licenses.activeOn', { server: entitlement.activation.serverName })
                  : t('licenses.inactive')}
              </p>

              {/* Le délai est affiché en clair : « déplaçable dans 23 heures » plutôt
                  qu'un bouton grisé sans explication. */}
              {entitlement.cooldownUntil ? (
                <div className="banner banner-info">
                  <strong>
                    {t('licenses.cooldown', {
                      duration: formatDuration(entitlement.cooldownUntil),
                    })}
                  </strong>
                  <p className="hint">{t('licenses.cooldownHint')}</p>
                </div>
              ) : null}

              {available.length === 0 ? (
                <Empty>{t('licenses.noServer')}</Empty>
              ) : (
                <div className="row wrap">
                  <Select
                    aria-label={t('licenses.chooseServer')}
                    value={target}
                    onChange={(event) =>
                      setTargets((previous) => ({
                        ...previous,
                        [entitlement.id]: event.target.value,
                      }))
                    }
                  >
                    {available.map((server) => (
                      <option key={server.id} value={server.id}>
                        {server.name}
                      </option>
                    ))}
                  </Select>

                  {entitlement.canActivate ? (
                    <Button
                      variant="primary"
                      pending={busy}
                      onClick={() =>
                        void activate.run({ id: entitlement.id, serverId: target }).then(refresh)
                      }
                    >
                      {t('licenses.activate')}
                    </Button>
                  ) : null}

                  {entitlement.canTransfer ? (
                    <Button
                      pending={busy}
                      disabled={target === entitlement.activation?.serverId}
                      onClick={() =>
                        void transfer.run({ id: entitlement.id, serverId: target }).then(refresh)
                      }
                    >
                      {t('licenses.transfer')}
                    </Button>
                  ) : null}

                  {entitlement.activation ? (
                    <ConfirmButton
                      message={t('licenses.releaseConfirm')}
                      pending={busy}
                      onConfirm={() => void release.run(entitlement.id).then(refresh)}
                    >
                      {t('licenses.release')}
                    </ConfirmButton>
                  ) : null}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
